import type { ShpeshftDocument } from './document';

export type Rgb = { r: number; g: number; b: number };
export type Hsl = { h: number; s: number; l: number };

const clamp = (value: number, minimum = 0, maximum = 1) => Math.min(maximum, Math.max(minimum, value));
const byte = (value: number) => Math.round(clamp(value) * 255).toString(16).padStart(2, '0');

export function normalizeHex(value: string): string | null {
  const raw = value.trim().replace(/^#/, '');
  const expanded = raw.length === 3 ? raw.split('').map((character) => character + character).join('') : raw;
  return /^[0-9a-f]{6}$/i.test(expanded) ? `#${expanded.toUpperCase()}` : null;
}

export function hexToRgb(value: string): Rgb {
  const hex = normalizeHex(value) ?? '#000000';
  return { r: parseInt(hex.slice(1, 3), 16), g: parseInt(hex.slice(3, 5), 16), b: parseInt(hex.slice(5, 7), 16) };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  return `#${byte(r / 255)}${byte(g / 255)}${byte(b / 255)}`.toUpperCase();
}

export function hexToHsv(value: string): { h: number; s: number; v: number } {
  const { r, g, b } = hexToRgb(value), red = r / 255, green = g / 255, blue = b / 255;
  const maximum = Math.max(red, green, blue), minimum = Math.min(red, green, blue), delta = maximum - minimum;
  let hue = 0;
  if (delta) {
    if (maximum === red) hue = 60 * (((green - blue) / delta) % 6);
    else if (maximum === green) hue = 60 * ((blue - red) / delta + 2);
    else hue = 60 * ((red - green) / delta + 4);
  }
  return { h: (hue + 360) % 360, s: maximum ? delta / maximum : 0, v: maximum };
}

export function hsvToHex(h: number, s: number, v: number): string {
  const hue = ((h % 360) + 360) % 360, saturation = clamp(s), value = clamp(v), chroma = value * saturation;
  const x = chroma * (1 - Math.abs((hue / 60) % 2 - 1)), match = value - chroma;
  const [red, green, blue] = hue < 60 ? [chroma, x, 0] : hue < 120 ? [x, chroma, 0] : hue < 180 ? [0, chroma, x] : hue < 240 ? [0, x, chroma] : hue < 300 ? [x, 0, chroma] : [chroma, 0, x];
  return rgbToHex({ r: (red + match) * 255, g: (green + match) * 255, b: (blue + match) * 255 });
}

export function hexToHsl(value: string): Hsl {
  const { r, g, b } = hexToRgb(value), red = r / 255, green = g / 255, blue = b / 255;
  const maximum = Math.max(red, green, blue), minimum = Math.min(red, green, blue), delta = maximum - minimum;
  const lightness = (maximum + minimum) / 2;
  let hue = 0;
  if (delta) {
    if (maximum === red) hue = 60 * (((green - blue) / delta) % 6);
    else if (maximum === green) hue = 60 * ((blue - red) / delta + 2);
    else hue = 60 * ((red - green) / delta + 4);
  }
  return { h: (hue + 360) % 360, s: delta ? delta / (1 - Math.abs(2 * lightness - 1)) : 0, l: lightness };
}

export function hslToHex(h: number, s: number, l: number): string {
  const hue = ((h % 360) + 360) % 360, saturation = clamp(s), lightness = clamp(l);
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = chroma * (1 - Math.abs((hue / 60) % 2 - 1)), match = lightness - chroma / 2;
  const [red, green, blue] = hue < 60 ? [chroma, x, 0] : hue < 120 ? [x, chroma, 0] : hue < 180 ? [0, chroma, x] : hue < 240 ? [0, x, chroma] : hue < 300 ? [x, 0, chroma] : [chroma, 0, x];
  return rgbToHex({ r: (red + match) * 255, g: (green + match) * 255, b: (blue + match) * 255 });
}

export function snapPercentEndpoint(value: number, threshold = 2): number {
  if (value <= threshold) return 0;
  if (value >= 100 - threshold) return 100;
  return Math.min(100, Math.max(0, value));
}

export const CANONICAL_HUES = [0, 30, 60, 120, 180, 240, 280, 320, 360] as const;

export function magneticHue(value: number, tolerance = 4): number {
  const nearest = CANONICAL_HUES.reduce((best, hue) => Math.abs(hue - value) < Math.abs(best - value) ? hue : best, CANONICAL_HUES[0]);
  return Math.abs(nearest - value) <= tolerance ? nearest % 360 : value;
}

export function documentColours(document: ShpeshftDocument): string[] {
  const colours = new Set<string>();
  for (const id of document.order) {
    const style = document.objects[id]?.style;
    if (!style) continue;
    if (style.fillEnabled) colours.add(normalizeHex(style.fill) ?? style.fill);
    if (style.strokeEnabled) colours.add(normalizeHex(style.strokeColor) ?? style.strokeColor);
  }
  return [...colours];
}
